import { ShapeGeometryKind, TriangleDirection } from '../../../core/dto';

export interface IShapeOutline {
  kind: ShapeGeometryKind;
  xIn: number;
  yIn: number;
  direction: TriangleDirection;
}

/** The effective second dimension: single-dimension kinds are as tall as they are wide. */
export function outlineExtentsIn(g: IShapeOutline): { x: number; y: number } {
  const twoDim = g.kind === 'RoundedSlot' || g.kind === 'SquareSlot' || g.kind === 'Triangle';
  return { x: g.xIn, y: twoDim ? g.yIn : g.xIn };
}

/**
 * SVG path for a hole outline in screen coordinates (y grows DOWN), centered on (cx, cy),
 * scale in px per inch. `apexUp` is the triangle apex direction already resolved to screen
 * terms by the caller (entry direction XOR shape-option flipY XOR the view's y inversion) —
 * this function knows nothing about frames.
 */
export function shapeOutlinePath(
  g: IShapeOutline,
  cx: number,
  cy: number,
  scale: number,
  apexUp: boolean
): string {
  const ext = outlineExtentsIn(g);
  const hw = (ext.x / 2) * scale;
  const hh = (ext.y / 2) * scale;

  switch (g.kind) {
    case 'Round': {
      const r = hw;
      return `M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0 Z`;
    }
    case 'Square':
    case 'SquareSlot':
      return `M ${cx - hw} ${cy - hh} h ${2 * hw} v ${2 * hh} h ${-2 * hw} Z`;
    case 'RoundedSlot': {
      // Obround: a rounded rect whose corner radius is the half-extent of the short axis, so
      // the rounded ends always cap the long axis. Equal extents degenerate to a circle.
      const r = Math.min(hw, hh);
      return [
        `M ${cx - hw + r} ${cy - hh}`,
        `h ${2 * (hw - r)}`,
        `a ${r} ${r} 0 0 1 ${r} ${r}`,
        `v ${2 * (hh - r)}`,
        `a ${r} ${r} 0 0 1 ${-r} ${r}`,
        `h ${-2 * (hw - r)}`,
        `a ${r} ${r} 0 0 1 ${-r} ${-r}`,
        `v ${-2 * (hh - r)}`,
        `a ${r} ${r} 0 0 1 ${r} ${-r}`,
        'Z',
      ].join(' ');
    }
    case 'Triangle': {
      // Isosceles: base spans the X extent, apex centered. Screen y grows down, so "up" is -hh.
      const baseY = apexUp ? cy + hh : cy - hh;
      const apexY = apexUp ? cy - hh : cy + hh;
      return `M ${cx - hw} ${baseY} L ${cx + hw} ${baseY} L ${cx} ${apexY} Z`;
    }
  }
}

/**
 * The outline as a polygon in INCH coordinates centered on origin, part frame (y grows UP),
 * counter-clockwise — for the 3D extrusion path. Curves are tessellated at a step count
 * proportional to their angular span (`segmentsPerCircle` for a full turn), so a full circle
 * and a slot's half-circle caps come out equally smooth.
 */
export function shapeOutlinePointsIn(
  g: IShapeOutline,
  apexPositive: boolean,
  segmentsPerCircle = 48
): { x: number; y: number }[] {
  const ext = outlineExtentsIn(g);
  const hw = ext.x / 2;
  const hh = ext.y / 2;

  const arc = (cx: number, cy: number, r: number, a0: number, a1: number) => {
    const steps = Math.max(2, Math.round((Math.abs(a1 - a0) / (2 * Math.PI)) * segmentsPerCircle));
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i <= steps; i++) {
      const a = a0 + ((a1 - a0) * i) / steps;
      pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
    }
    return pts;
  };

  switch (g.kind) {
    case 'Round':
      return arc(0, 0, hw, 0, 2 * Math.PI).slice(0, -1);
    case 'Square':
    case 'SquareSlot':
      return [
        { x: hw, y: -hh },
        { x: hw, y: hh },
        { x: -hw, y: hh },
        { x: -hw, y: -hh },
      ];
    case 'RoundedSlot': {
      const r = Math.min(hw, hh);
      if (hw >= hh) {
        // Caps on the left/right ends.
        return [
          ...arc(hw - r, 0, r, -Math.PI / 2, Math.PI / 2),
          ...arc(-(hw - r), 0, r, Math.PI / 2, (3 * Math.PI) / 2),
        ];
      }
      // Caps on the top/bottom ends.
      return [
        ...arc(0, hh - r, r, 0, Math.PI),
        ...arc(0, -(hh - r), r, Math.PI, 2 * Math.PI),
      ];
    }
    case 'Triangle': {
      const baseY = apexPositive ? -hh : hh;
      const apexY = apexPositive ? hh : -hh;
      return apexPositive
        ? [
            { x: hw, y: baseY },
            { x: 0, y: apexY },
            { x: -hw, y: baseY },
          ]
        : [
            { x: -hw, y: baseY },
            { x: 0, y: apexY },
            { x: hw, y: baseY },
          ];
    }
  }
}
